import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  Play,
  X,
  Coffee,
  Video,
  Users,
  Calendar,
  MessageCircle,
  BookOpen,
  ChevronDown,
  Menu,
  Mail,
  Globe,
  Briefcase,
  Heart,
  Compass,
  Home,
  ArrowRight,
  Instagram,
  Youtube,
  Facebook,
} from "lucide-react";

// TikTok icon component (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./components/ui/dialog";

// Content data
const communityFeatures = [
  {
    icon: Video,
    title: "Live Swedish Workshops",
    description:
      "Interactive and fun sessions designed to help you explore key topics, practise pronunciation, and ask questions live.",
    color: "bg-[#E8F0EC]",
    iconColor: "text-[#4A7C8A]",
  },
  {
    icon: Coffee,
    title: "Digital Fika Coffee Dates",
    description:
      "Practise casual conversations in Swedish with fellow learners around the world in a relaxed, welcoming setting.",
    color: "bg-[#FEF9E8]",
    iconColor: "text-[#B8860B]",
  },
  {
    icon: BookOpen,
    title: "A Vault of Resources",
    description:
      "Access curated material to help you master Swedish grammar, vocabulary, everyday questions, and cultural nuances.",
    color: "bg-[#E8F0EC]",
    iconColor: "text-[#5B8A6A]",
  },
  {
    icon: Users,
    title: "Structured Lessons",
    description:
      "Follow a proven step-by-step framework from Swedish for beginners to confident conversations.",
    color: "bg-[#E8EEF0]",
    iconColor: "text-[#4A7C8A]",
  },
  {
    icon: MessageCircle,
    title: "Supportive Community",
    description:
      "Ask questions, get feedback, and connect with others who are passionate about learning Swedish.",
    color: "bg-[#F0EDE8]",
    iconColor: "text-[#8A6B4A]",
  },
];

const targetAudiences = [
  {
    icon: Globe,
    title: "Living Abroad",
    description:
      "Missing chances to speak Swedish in daily life? Our digital fika sessions bring real Swedish speaking practice to your screen.",
  },
  {
    icon: Briefcase,
    title: "Preparing for Sweden",
    description:
      "Moving for work, studies, or a new chapter? Get ahead with practical Swedish lessons for everyday conversations.",
  },
  {
    icon: Heart,
    title: "Swedish Roots or Partner",
    description:
      "Reconnect with your heritage or communicate with a Swedish partner and their family.",
  },
  {
    icon: Compass,
    title: "Curious Explorers",
    description:
      "Just curious about Sweden? Our free community is a fun, welcoming place to start your journey.",
  },
  {
    icon: Home,
    title: "Already in Sweden",
    description:
      "Living here but stuck on survival Swedish? Improve your fluency and thrive in professional settings.",
  },
];

const faqs = [
  {
    question: "Do I need to be a beginner to join?",
    answer:
      "No. Beginners and intermediate learners are welcome. You will get structured Swedish conversation practice that fits your level, and you can join workshops and fika sessions that match where you are right now.",
    hasJoinButton: true,
  },
  {
    question: "Will I get speaking practice, not just theory?",
    answer:
      "Yes. This community is built around Swedish speaking practice. You will practise speaking in workshops, fika sessions, and structured activities designed to help you use Swedish actively.",
    hasJoinButton: false,
  },
  {
    question: "Is it really free?",
    answer:
      "Yes. Joining the community is free. You can participate, access resources, and join practice events that are included for all members.",
    hasJoinButton: false,
  },
  {
    question: "How do I access workshops and events?",
    answer:
      "After you join the Skool community, you will see updates, posts, and event details inside the community. You will also receive email updates with information about upcoming Swedish workshops and practice sessions.",
    hasJoinButton: false,
  },
  {
    question: "What kind of Swedish will I learn?",
    answer:
      "You will learn practical Swedish for everyday life and real conversations. You will work on vocabulary, pronunciation, and grammar in context, so you can speak more naturally and confidently.",
    hasJoinButton: false,
  },
  {
    question: "Will your community really help me improve?",
    answer:
      "Yes, our community will give you the tools you need to improve. But how, if, and whether you improve — that is up to you. It all depends on the level of your commitment. If you are committed to improving, and using the tools, joining the workshops, participating in the daily coffees, joining the group sessions or the one-on-one lessons, then there is absolutely no reason why you wouldn't be able to succeed. I can guarantee it.",
    hasJoinButton: false,
  },
];

const methodSteps = [
  {
    step: "1",
    title: "Meaningful Context",
    description:
      "Every lesson begins with real-world situations that give purpose to what you're learning.",
  },
  {
    step: "2",
    title: "Practical Introduction",
    description:
      "You're introduced to Swedish phrases and structures you can use immediately.",
  },
  {
    step: "3",
    title: "Interactive Practice",
    description:
      "Guided speaking practice that's engaging, supportive, and builds real confidence.",
  },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Community", href: "#community" },
    { label: "Methodology", href: "#methodology" },
    { label: "About Tangi", href: "#about" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-header shadow-md" : "bg-transparent"
      }`}
    >
      <nav className="container-width py-4">
        <div className="flex items-center justify-between">
          <a
            href="#"
            className="text-xl md:text-2xl font-bold text-[#4A7C8A]"
            data-testid="logo-link"
          >
            <span className="font-accent text-2xl md:text-3xl">Swedish</span>{" "}
            with Confidence
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-slate-700 hover:text-[#4A7C8A] font-medium"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://swedishwithconfidence.setmore.com/services/5bdcb1c6-3133-492b-b56b-bf5e6d966d89"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-call-btn"
            >
              <Button className="btn-primary text-sm px-6 py-2">
                Book a Call
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-[#4A7C8A]" />
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4 pb-4 border-t border-slate-200"
            >
              <div className="flex flex-col gap-4 pt-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-slate-700 hover:text-[#005B99] font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href="https://swedishwithconfidence.setmore.com/services/5bdcb1c6-3133-492b-b56b-bf5e6d966d89"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-book-call-btn"
                >
                  <Button className="btn-primary w-full">Book a Call</Button>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

// Hero Section
const HeroSection = ({ onPlayVideo }) => {
  return (
    <section className="min-h-screen flex items-center pt-20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#E0F2FE]/50 to-transparent -z-10" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#FEFCE8]/60 rounded-full blur-3xl -z-10" />

      <div className="container-width">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-left"
          >
            <motion.p
              variants={fadeInUp}
              className="text-[#4A7C8A] font-medium mb-4 tracking-wide uppercase text-sm"
            >
              100% Free to Join
            </motion.p>

            <motion.h1
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A4D] leading-tight mb-6"
            >
              Master <span className="gradient-text">Swedish</span>
              <br />
              with Confidence
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed max-w-lg"
            >
              Join Tangi Davidsson and a supportive community of learners. Start
              conversing from day one with our proven method.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a
                href="https://www.skool.com/learn-swedish-8335"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-join-community-btn"
              >
                <Button className="btn-primary flex items-center gap-2">
                  Join Free Community
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
              <a
                href="https://swedishwithconfidence.setmore.com/services/5bdcb1c6-3133-492b-b56b-bf5e6d966d89"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-call-btn"
              >
                <Button
                  variant="outline"
                  className="border-2 border-[#4A7C8A] text-[#4A7C8A] px-8 py-4 rounded-full hover:bg-[#4A7C8A]/5"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book a Welcome Call
                </Button>
              </a>
            </motion.div>
          </motion.div>

          {/* Right - Video Thumbnail */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative"
          >
            <div className="video-container relative aspect-video bg-slate-900 rounded-2xl overflow-hidden group cursor-pointer"
                 onClick={onPlayVideo}
                 data-testid="video-thumbnail">
              {/* Video Thumbnail Background */}
              <img
                src="https://img.youtube.com/vi/PaUZpBNGQiM/maxresdefault.jpg"
                alt="Welcome to Learning Swedish with Tangi"
                className="w-full h-full object-cover"
              />

              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-all">
                <button
                  className="play-button w-20 h-20 bg-[#4A7C8A] rounded-full flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform"
                  data-testid="play-video-btn"
                >
                  <Play className="w-8 h-8 ml-1" fill="white" />
                </button>
              </div>

              {/* Watch Video Label */}
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="text-sm font-medium text-slate-800">
                  Watch Welcome Video
                </span>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#A8C5B4] rounded-full opacity-20 blur-xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#4A7C8A] rounded-full opacity-20 blur-xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Community Features Section
const CommunitySection = () => {
  return (
    <section id="community" className="section-padding bg-white">
      <div className="container-width">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-[#4A7C8A] font-medium mb-4 tracking-wide uppercase text-sm"
          >
            About Our Community
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-5xl font-bold text-[#1E3A4D] mb-6"
          >
            Everything You Need to Learn Swedish
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg text-slate-600 max-w-2xl mx-auto"
          >
            When you join the Swedish with Confidence community, you unlock a
            complete toolkit for learning Swedish online — at no cost.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="bento-grid"
        >
          {communityFeatures.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="feature-card"
              data-testid={`feature-card-${index}`}
            >
              <div
                className={`w-14 h-14 ${feature.color} rounded-xl flex items-center justify-center mb-6`}
              >
                <feature.icon className={`w-7 h-7 ${feature.iconColor}`} />
              </div>
              <h3 className="text-xl font-semibold text-[#1E3A4D] mb-3">
                {feature.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Join Community Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <a
            href="https://www.skool.com/learn-swedish-8335"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="community-section-join-btn"
          >
            <Button className="btn-primary flex items-center gap-2 mx-auto">
              Click here to join the community
              <ArrowRight className="w-5 h-5" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Methodology Section
const MethodologySection = () => {
  return (
    <section id="methodology" className="section-padding bg-[#FAFAF9]">
      <div className="container-width">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.p
              variants={fadeInUp}
              className="text-[#4A7C8A] font-medium mb-4 tracking-wide uppercase text-sm"
            >
              Our Teaching Style
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-5xl font-bold text-[#1E3A4D] mb-6"
            >
              How Every Lesson Works
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-slate-600 mb-8 leading-relaxed"
            >
              Our Swedish lessons are carefully designed around a structured
              methodology used by successful language educators worldwide.
            </motion.p>

            <motion.div variants={staggerContainer} className="space-y-6">
              {methodSteps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex gap-4 items-start"
                  data-testid={`method-step-${index}`}
                >
                  <div className="w-10 h-10 bg-[#4A7C8A] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    {step.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#1E3A4D] mb-1">
                      {step.title}
                    </h4>
                    <p className="text-slate-600">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Lesson Screenshots Collage */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10"
            >
              <p className="text-sm text-slate-500 mb-4 font-medium">Glimpses from workshops and recent lessons:</p>
              
              {/* Main workshop image */}
              <div className="rounded-2xl overflow-hidden shadow-lg mb-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_svenska-confidence/artifacts/rckxl3y0_image.png"
                  alt="Live Swedish workshop with students from around the world"
                  className="w-full h-auto object-contain"
                />
              </div>
              
              {/* Smaller lesson images */}
              <div className="grid grid-cols-3 gap-2 rounded-xl overflow-hidden">
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets.emergentagent.com/job_svenska-confidence/artifacts/imp215o8_image.png"
                    alt="Swedish lesson 1"
                    className="w-full h-auto object-contain bg-slate-900"
                  />
                </div>
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets.emergentagent.com/job_svenska-confidence/artifacts/m40svnoj_image.png"
                    alt="Swedish lesson 2"
                    className="w-full h-auto object-contain bg-slate-900"
                  />
                </div>
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets.emergentagent.com/job_svenska-confidence/artifacts/alucw7mq_image.png"
                    alt="Swedish lesson 3"
                    className="w-full h-auto object-contain bg-slate-900"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-3 italic">Live workshops with students from around the world</p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-2xl font-bold text-[#1E3A4D] mb-6">
                This Method Is Perfect For:
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-[#E8F0EC] rounded-xl">
                  <h4 className="font-semibold text-[#4A7C8A] mb-1">
                    Complete Beginners
                  </h4>
                  <p className="text-slate-600 text-sm">
                    Who want a solid foundation with clear structure from the
                    very first lesson.
                  </p>
                </div>
                <div className="p-4 bg-[#FEF9E8] rounded-xl">
                  <h4 className="font-semibold text-[#8A6B4A] mb-1">
                    Learners Seeking More Practice
                  </h4>
                  <p className="text-slate-600 text-sm">
                    Who crave real Swedish conversation practice beyond
                    textbooks and apps.
                  </p>
                </div>
                <div className="p-4 bg-[#E8EEF0] rounded-xl">
                  <h4 className="font-semibold text-[#5B8A6A] mb-1">
                    Stuck Memorizers
                  </h4>
                  <p className="text-slate-600 text-sm">
                    Who have been memorising vocabulary but want to finally
                    start actually speaking Swedish.
                  </p>
                </div>
              </div>
            </div>

            {/* Decorative Swedish flag colors */}
            <div className="absolute -z-10 -top-4 -left-4 w-full h-full bg-gradient-to-br from-[#4A7C8A]/10 to-[#A8C5B4]/10 rounded-2xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Teacher Section
const AboutSection = () => {
  return (
    <section id="about" className="section-padding bg-white">
      <div className="container-width">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.p
            variants={fadeInUp}
            className="text-[#4A7C8A] font-medium mb-4 tracking-wide uppercase text-sm"
          >
            Meet Your Teacher
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-5xl font-bold text-[#1E3A4D]"
          >
            Tangi Davidsson
          </motion.h2>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex-shrink-0"
          >
            <div className="w-72 h-72 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-[#A8C5B4]/30">
              <img
                src="https://customer-assets.emergentagent.com/job_svenska-confidence/artifacts/ulf3yb54_channels4_profile.jpg"
                alt="Tangi Davidsson - Swedish Teacher"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="bg-gradient-to-br from-[#FAFAF9] to-white p-8 rounded-2xl shadow-lg border border-slate-100 flex-1"
          >
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              Hi, I'm Tangi Davidsson, a Swedish teacher with <span className="font-semibold text-[#005B99]">over 10 years of experience</span> and the host of Swedish with Confidence.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              I've worked with <span className="font-semibold text-[#005B99]">thousands of students</span> at different levels — from complete beginners taking their first steps to those looking to upscale and refine their Swedish.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              Using the <span className="font-semibold text-[#005B99]">4Ps methodology</span> and conversational speaking, I help my students understand their needs, create a long-term plan to reach their goals, and strengthen their Swedish-speaking capabilities.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              In this community, you'll get guidance, correction, and encouragement so you can start using Swedish in everyday situations — whether you're ordering coffee, chatting with colleagues, or telling a story to new friends.
            </p>

            <a
              href="https://swedishwithconfidence.setmore.com/services/5bdcb1c6-3133-492b-b56b-bf5e6d966d89"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-book-call-btn"
            >
              <Button className="btn-secondary flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Book a Welcome Call with Tangi
              </Button>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Target Audience Section
const AudienceSection = () => {
  return (
    <section className="section-padding bg-[#3D5A6C]">
      <div className="container-width">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-[#A8C5B4] font-medium mb-4 tracking-wide uppercase text-sm"
          >
            Who Is This For?
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-5xl font-bold text-white mb-6"
          >
            Find Your Path to Swedish
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg text-white/80 max-w-2xl mx-auto"
          >
            If you've ever wished you could speak Swedish naturally — whether
            for travel, work, personal connection, or just curiosity — this
            community is for you.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {targetAudiences.map((audience, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all"
              data-testid={`audience-card-${index}`}
            >
              <div className="w-12 h-12 bg-[#A8C5B4] rounded-xl flex items-center justify-center mb-4">
                <audience.icon className="w-6 h-6 text-[#1E3A4D]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {audience.title}
              </h3>
              <p className="text-white/70">{audience.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section id="faq" className="section-padding bg-[#FAFAF9]">
      <div className="container-width">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-[#4A7C8A] font-medium mb-4 tracking-wide uppercase text-sm"
          >
            Frequently Asked Questions
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-5xl font-bold text-[#1E3A4D] mb-6"
          >
            Got Questions?
          </motion.h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="faq-item px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-lg font-medium text-[#1E3A4D] hover:text-[#4A7C8A] py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                  {faq.answer}
                  {faq.hasJoinButton && (
                    <a
                      href="https://www.skool.com/learn-swedish-8335"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-4"
                      data-testid="faq-join-btn"
                    >
                      <Button className="bg-[#4A7C8A] text-white px-5 py-2 rounded-full text-sm hover:bg-[#3D6B78] transition-all">
                        Click here to join
                      </Button>
                    </a>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="section-padding bg-white">
      <div className="container-width">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-[#3D5A6C] to-[#4A7C8A] rounded-3xl p-8 md:p-16 text-center relative overflow-hidden"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#A8C5B4] rounded-full opacity-10 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full opacity-10 blur-3xl" />

          <div className="relative z-10">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Start Speaking Swedish?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Joining is easy and completely free. Watch the video, join the
              community, then book a welcome call if you want a clear, personal
              starting plan.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.skool.com/learn-swedish-8335"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-join-community-btn"
              >
                <Button className="btn-secondary flex items-center gap-2">
                  Join Free Community
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
              <a
                href="https://swedishwithconfidence.setmore.com/services/5bdcb1c6-3133-492b-b56b-bf5e6d966d89"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-book-call-btn"
              >
                <Button
                  variant="outline"
                  className="border-2 border-white text-white px-8 py-4 rounded-full hover:bg-white/10"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book a Welcome Call
                </Button>
              </a>
            </div>

            <p className="text-white/60 mt-8 font-accent text-2xl">
              See you inside… hej hej! 🇸🇪
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section className="py-16 bg-[#1E3A4D]">
      <div className="container-width">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-2xl font-bold text-white mb-2">
              Swedish with Confidence
            </h3>
            <p className="text-slate-400">Learning Swedish with Tangi</p>
          </div>

          <div className="flex items-center gap-6">
            <a
              href="mailto:Tangi.davidsson@gmail.com"
              className="flex items-center gap-2 text-white hover:text-[#A8C5B4] transition-colors"
              data-testid="contact-email-link"
            >
              <Mail className="w-5 h-5" />
              <span>Tangi.davidsson@gmail.com</span>
            </a>
          </div>

          {/* Social Media Links */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.instagram.com/swedishwithconfidence/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-[#A8C5B4] hover:text-[#1E3A4D] transition-all"
              data-testid="social-instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.youtube.com/@learnswedishwithconfidence"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-[#A8C5B4] hover:text-[#1E3A4D] transition-all"
              data-testid="social-youtube"
            >
              <Youtube className="w-5 h-5" />
            </a>
            <a
              href="https://www.facebook.com/swediswithconfidence"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-[#A8C5B4] hover:text-[#1E3A4D] transition-all"
              data-testid="social-facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="https://www.tiktok.com/@swedishwithconfidence"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-[#A8C5B4] hover:text-[#1E3A4D] transition-all"
              data-testid="social-tiktok"
            >
              <TikTokIcon className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-slate-700 text-center text-slate-500">
          <p>© {new Date().getFullYear()} Swedish with Confidence. All rights reserved.</p>
        </div>
      </div>
    </section>
  );
};

// Video Modal Component
const VideoModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 bg-black border-none overflow-hidden">
        <DialogTitle className="sr-only">Welcome Video</DialogTitle>
        <DialogDescription className="sr-only">
          Watch the welcome video from Tangi Davidsson to learn about Swedish with Confidence community
        </DialogDescription>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          data-testid="close-video-modal-btn"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="aspect-video">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/PaUZpBNGQiM?rel=0&autoplay=1"
            title="Welcome to Learning Swedish with Tangi"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main App Component
function App() {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  return (
    <div className="App">
      <Navigation />
      <HeroSection onPlayVideo={() => setIsVideoModalOpen(true)} />
      <CommunitySection />
      <MethodologySection />
      <AboutSection />
      <AudienceSection />
      <FAQSection />
      <CTASection />
      <ContactSection />
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
      />
    </div>
  );
}

export default App;
